// TODO: [ATL-3026] Refactor component to use only css instead of svg.
import React, { Component } from 'react'
import cx from 'classnames'
import isFunction from 'lodash/isFunction'
import PropTypes from 'prop-types'

import { IconStatusWrapper } from '../icon-status-wrapper/icon-status-wrapper'

import styles from './compact-list-row.module.css'

const propTypes = {
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
    className: PropTypes.string,
    contentClassName: PropTypes.string,
    expandedView: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    iconClassName: PropTypes.string,
    iconWrapperClassName: PropTypes.string,
    id: PropTypes.string.isRequired,
    inactive: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    isCollapsingDisabled: PropTypes.bool,
    isListVirtual: PropTypes.bool,
    isSelected: PropTypes.bool,
    status: PropTypes.string,
    statusTooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
}

const defaultProps = {
    className: '',
    contentClassName: '',
    icon: null,
    iconClassName: '',
    iconWrapperClassName: '',
    inactive: false,
    isCollapsingDisabled: false,
    isListVirtual: false,
    isSelected: false,
    status: null
}

/**
 * A row component that can be displayed within the {@link CompactList}.
 *
 * {@link https://confluence.tomtomgroup.com/display/TTB/Molecules#Molecules-Rows | Row UX Design}
 *
 * @example
 * <CompactListRow
 *     icon={<AnySvgIcon />}
 *     onSelected={this.select}>
 *     <h4>My title</h4>
 *     <strong>My text which is so lorem ipsum</strong>
 * </CompactListRow>
 *
 * @property {ReactElement|ReactElement[]} children - The children of this row.
 * @property {string} id                            - The id of the row, which will be passed to the
 *                                                    CompactList's onSelect callback as first argument.
 * @property {ReactNode|Function} [icon=null]       - The icon to put into the circle or a function which creates it.
 * @property {boolean} [isSelected=false]           - Indicates if the row is selected or not.
 * @property {boolean|Function} [inactive=false]    - Indicates that the row is inactive or not.
 * @property {string} [className='']                - A CSS class to append to the component.
 * @property {string} [iconClassName='']
 * @property {string} [iconWrapperClassName='']
 * @property {string} [contentClassName='']
 * @property {string} [status=null]                 - The status to use for the status bubble, which must match
 *                                                    a status given to the statuses prop of CompactList.
 * @property {string} [statusTooltip=null]          - Tooltip to describe the status.
 * @property {boolean} [isListVirtual=false]        - Internal Property! A flag which indicates
 *                                                    that the list is virtualized.
 * @property {ReactElement|ReactElement[]} [expandedView] - the children of the expanded view
 */
class CompactListRow extends Component {
    constructor(props) {
        super(props)

        this.state = {
            isCollapsed: false
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!nextProps.isSelected) {
            this.setState({
                isCollapsed: false
            })
        }
    }

    render() {
        const {
            children,
            contentClassName,
            expandedView,
            icon,
            iconClassName,
            iconWrapperClassName,
            inactive,
            isCollapsingDisabled,
            isListVirtual,
            isSelected,
            status,
            statusTooltip
        } = this.props
        const { isCollapsed } = this.state
        // if a row is expandable, the virtual list does not work as expected
        // @link https://jira.ttt-sp.com/browse/ATL-5200
        const isExpandable = typeof expandedView === 'object' && isSelected

        const className = cx(
            styles['compact-list-row'],
            { [styles['compact-list-row-selected']]: isSelected },
            this.props.className
        )

        const inactiveOverlay = (isFunction(inactive) ? inactive() : inactive) ? (
            <div className={styles['compact-list-row-overlay-inactive']} />
        ) : null

        const iconWrapperClassNames = cx(
            styles['compact-list-row-icon-wrapper'],
            iconWrapperClassName,
            isListVirtual || isExpandable ? styles['fixed-top-padding'] : styles['vertical-centered']
        )

        return (
            <div
                className={styles['compact-list-row-wrapper']}
                onClick={() => this.toggleCollapsed(isExpandable, isCollapsingDisabled)}
            >
                {inactiveOverlay}
                <div className={className}>
                    <div className={iconWrapperClassNames}>
                        <IconStatusWrapper
                            className={cx(styles['compact-list-row-icon'], iconClassName)}
                            toolTipText={statusTooltip}
                            {...{ status }}
                        >
                            {isFunction(icon) ? icon() : icon}
                        </IconStatusWrapper>
                    </div>
                    <div className={cx(styles['compact-list-row-content'], contentClassName)}>
                        {children}
                        {isExpandable && !isCollapsed && expandedView}
                    </div>
                </div>
            </div>
        )
    }

    toggleCollapsed(isExpandable, isCollapsingDisabled) {
        if (!isExpandable || isCollapsingDisabled) {
            return
        }
        const { isCollapsed } = this.state
        this.setState({
            isCollapsed: !isCollapsed
        })
    }
}

CompactListRow.propTypes = propTypes
CompactListRow.defaultProps = defaultProps

export default CompactListRow
